import type { FragmentData } from "@gqlb/core";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import NextImage from "next/image";
import NextLink from "next/link";
import { useEffect, useRef, useState } from "react";
import type { ChefSpecials_Fragment } from "@/components/pages/home/ChefSpecials";
import { ResponsiveImage } from "@/components/ResponsiveImage";
import FARM_TO_TABLE_IMG from "@/img/farm_to_table.png";

gsap.registerPlugin(ScrollTrigger);

export const FeaturedMeals: React.FC<{
  data: FragmentData<typeof ChefSpecials_Fragment>;
}> = ({ data }) => {
  const sectionRef = useRef<HTMLElement>(null);
  const headingRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const products = data.chefSpecials?.products ?? [];
  const product = products[activeIndex];

  useEffect(() => {
    if (!sectionRef.current) return;
    const ctx = gsap.context(() => {
      gsap.fromTo(
        headingRef.current,
        { y: 40, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          scrollTrigger: { trigger: headingRef.current, start: "top 85%" },
        },
      );
      gsap.fromTo(
        contentRef.current,
        { y: 60, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.9,
          scrollTrigger: { trigger: contentRef.current, start: "top 80%" },
        },
      );
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  useEffect(() => {
    if (!product || products.length <= 1) return;
    const timer = setTimeout(() => {
      setActiveIndex((prev) => (prev + 1) % products.length);
    }, 8000);
    return () => clearTimeout(timer);
  }, [product, products.length]);

  if (!product) return null;

  return (
    <section ref={sectionRef} className="py-20 md:py-32 px-6 max-w-7xl mx-auto">
      <div ref={headingRef} className="text-center mb-16 opacity-0">
        <p className="text-brand-mint font-semibold tracking-widest uppercase text-sm mb-3">
          Chef Specials
        </p>
        <h2 className="text-3xl md:text-5xl font-bold tracking-tight">
          This Week&apos;s Favorites
        </h2>
      </div>
      <div
        ref={contentRef}
        className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start opacity-0"
      >
        <div className="relative rounded-3xl overflow-hidden">
          <ResponsiveImage
            key={product.id}
            image={product.images[0]}
            objectFit="cover"
            className="relative w-full aspect-square lg:aspect-[4/3]"
          />
          <NextImage
            src={FARM_TO_TABLE_IMG}
            alt="Farm to Table"
            className="absolute bottom-4 right-4 z-10 w-24 md:w-32 xl:w-40 drop-shadow-lg"
          />
        </div>
        <div className="flex flex-col items-center lg:items-start text-center lg:text-left">
          <div className="flex items-center gap-3 mb-6">
            {products.map((p, i) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setActiveIndex(i)}
                className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
                  i === activeIndex ? "bg-brand-mint w-10" : "bg-gray-300 w-4"
                }`}
              />
            ))}
          </div>
          <h3 className="text-2xl md:text-4xl font-bold mb-2">{product.name}</h3>
          <p className="text-gray-500 text-lg leading-relaxed mb-8 max-w-md">
            {product.description}
          </p>
          <div className="flex gap-4">
            <NextLink
              href={`/product/${product.slug}`}
              className="px-8 py-3 bg-brand-mint text-brand-black font-semibold rounded-full hover:scale-105 transition-transform"
            >
              Order Now
            </NextLink>
            <button
              type="button"
              className="px-6 py-3 border-2 border-gray-300 rounded-full font-medium hover:border-brand-mint transition-colors cursor-pointer"
              onClick={() => setActiveIndex((prev) => (prev + 1) % products.length)}
            >
              Next &rarr;
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
